import React, { FunctionComponent, useContext } from "react";
import { Button } from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../../helpers";
import { AppContext } from "../../app/store";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../app/contexts/Analytics/types";

export interface ReviewBoxItem {
  label: string;
  value: string | string[];
  optional?: boolean;
  hideLabel?: boolean;
}
interface ReviewBoxSection {
  title: string;
  items: ReviewBoxItem[];
}
interface ReviewBoxProps {
  goToPage: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  items: { [key: string]: ReviewBoxSection } | ReviewBoxItem[];
  title: string;
}

const ReviewBox: FunctionComponent<ReviewBoxProps> = ({
  goToPage,
  items,
  title,
}) => {
  const { dispatch } = useContext(AppContext);
  const t = useTranslate();

  const ReviewBoxItemComponent: FunctionComponent<{
    item: ReviewBoxItem;
  }> = ({ item }) => (
    <li role="listitem">
      <p>
        {!item.hideLabel && item.label}
        {` ${item.optional ? t("enroll_form.optional") : ""}`}
      </p>
      {Array.isArray(item.value) ? (
        item.value.map((subValue, i) => <p key={i}>{subValue}</p>)
      ) : (
        <p>{item.value || t("enroll_form.review.chose_not_to_answer")}</p>
      )}
    </li>
  );

  const ReviewBoxItemListComponent: FunctionComponent<{
    items: ReviewBoxItem[];
  }> = ({ items }) => (
    <ul role="list">
      {items.map(item => (
        <ReviewBoxItemComponent key={item.label} item={item} />
      ))}
    </ul>
  );

  const ReviewBoxSectionComponent: FunctionComponent<{
    section: ReviewBoxSection;
  }> = ({ section }) => (
    <>
      <h3 className="ds-u-margin-left--3">{section.title}</h3>
      <ReviewBoxItemListComponent items={section.items} />
    </>
  );

  return (
    <div className="EnrollmentFormPage__reviewBox">
      <div className="ds-u-padding--2 ds-u-border-bottom--2 ds-u-border-color--gray ds-u-display--flex ds-u-justify-content--between ds-u-align-items--center">
        <h2 className="ds-text-heading--xl ds-u-margin--0">{title}</h2>
        <Button
          variation="ghost"
          className="ds-u-text-transform--capitalize ds-u-padding--0 ds-u-margin-left--1"
          onClick={e => {
            dispatch({
              type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
              settings: {
                button: {
                  buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                  buttonType: AnalyticsButtonType.BUTTON,
                  text: `${t("edit")} ${title}`,
                },
              },
            });
            goToPage(e);
          }}
          aria-label={`${t("edit")} ${title}`}
        >
          {t("edit")}
        </Button>
      </div>
      {Array.isArray(items) ? (
        <ReviewBoxItemListComponent items={items} />
      ) : (
        Object.keys(items).map(key => (
          <ReviewBoxSectionComponent key={key} section={items[key]} />
        ))
      )}
    </div>
  );
};

export default ReviewBox;
